import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { MenuButton } from "../../features/booking-history/detail/MenuButton";
import { TripDetailsCard } from "@/features/booking-history/detail/TripDetailsCard";
import { ContactDetailsCard } from "@/features/booking-history/detail/ContactDetailsCard";
import { HistoryCard } from "@/features/booking-history/detail/HistoryCard";
import { PassengerDetailsCard } from "@/features/booking-history/detail/PassengerDetailsCard";
import { useEffect, useState } from "react";
import type { BookingHistoryDetailData } from "@/types/booking-history/bookinghistorydetail";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth.store";
import {
  BookingHistoryDetails,
  ResendConfirmation,
} from "@/services/bookinghistory.service";
import { handleDownloadConfirmation } from "@/hooks/downloadConfirmation";
import { downloadTransactionReceipt } from "@/hooks/downloadTransactionReceipt";
import { BookingHistoryDetailsSkeleton } from "./BookingHistoryDetailsSkeleton ";
import { FullScreenLoader } from "@/components/common/FullScreenLoader";
import { BoardingPassCard } from "@/features/booking-history/detail/BoardingPassCard";
import { handleDownloadBoardingPass } from "@/hooks/downloadBoardingPass";

const isAllowBoardingPass = useAuthStore.getState().user?.isAllowBoardingPass;

const RESEND_OPTIONS = ["Booking Confirmation"];

const DOWNLOAD_OPTIONS = ["Booking Confirmation", "Transaction Receipt", ...(isAllowBoardingPass ? ["Boarding Pass"] : [])];

export default function BookingDetailsPage() {
  const navigate = useNavigate();
  const { paymentId } = useParams<{ paymentId: string }>();
  const [bookingDetail, setBookingDetail] =
    useState<BookingHistoryDetailData | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!paymentId) return;

    const fetchBookingDetails = async () => {
      try {
        setLoading(true);

        const response = await BookingHistoryDetails({
          agentCompanySellerId:
            useAuthStore.getState().user?.agentCompanySellerId || "",
          paymentId,
        });

        await new Promise((resolve) => setTimeout(resolve, 300));

        setBookingDetail(response.data);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to load booking details.",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchBookingDetails();
  }, [paymentId]);

  const handleResendConfirmation = async () => {
    if (!paymentId) {
      toast.error("Payment ID is missing.");
      return;
    }

    try {
      setActionLoading(true);

      const agentCompanySellerId =
        useAuthStore.getState().user?.agentCompanySellerId ?? "";

      if (!agentCompanySellerId) {
        toast.error("Agent company seller ID is missing.");
        return;
      }

      const response = await ResendConfirmation({
        paymentId,
        agentCompanySellerId,
      });

      if (response.status) {
        toast.success("Booking confirmation has been resent.");
      } else {
        toast.error(
          response.message ?? "Failed to resend booking confirmation.",
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to resend booking confirmation.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadBooking = async () => {
    if (!paymentId || actionLoading) return;

    try {
      setActionLoading(true);

      await handleDownloadConfirmation(paymentId);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download booking confirmation.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!paymentId || actionLoading) return;

    try {
      setActionLoading(true);

      await downloadTransactionReceipt(paymentId);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download booking receipt.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadBP = async () => {
    if (!paymentId || actionLoading) return;

    try {
      setActionLoading(true);

      await handleDownloadBoardingPass(paymentId);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download boarding pass.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <BookingHistoryDetailsSkeleton
        isReturn={bookingDetail?.booking?.returnDateTime != null}
        passengerCount={bookingDetail?.passengerSummaries?.length ?? 1}
      />
    );
  }

  if (!bookingDetail) {
    return (
      <div className="text-sm text-muted-foreground">
        Booking details not found.
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      {actionLoading && <FullScreenLoader />}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="cursor-pointer flex items-center gap-2 text-lg font-semibold text-secondary-foreground md:text-xl"
          >
            <ChevronLeft
              size={24}
              className="text-primary"
              aria-hidden="true"
            />
            Booking no. {bookingDetail.booking.bookingNo}
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:mt-0">
          <MenuButton
            label="Resend"
            options={RESEND_OPTIONS}
            onSelect={(option) => {
              if (option === "Booking Confirmation") {
                handleResendConfirmation();
              }
            }}
          />
          <MenuButton
            label="Download"
            options={DOWNLOAD_OPTIONS}
            onSelect={(option) => {
              if (option === "Booking Confirmation") {
                handleDownloadBooking();
              } else if (option === "Transaction Receipt") {
                handleDownloadReceipt();
              } else if (option === "Boarding Pass") {
                handleDownloadBP();
              }
            }}
          />
        </div>
      </div>

      {isAllowBoardingPass && (
        <BoardingPassCard
          passengers={bookingDetail.passengers}
          paymentId={paymentId}
        />
      )}
      
      <TripDetailsCard
        booking={bookingDetail.booking}
        passengersummary={bookingDetail.passengerSummaries ?? []}
        paymentId={paymentId}
      />

      <ContactDetailsCard
        contact={bookingDetail.contact}
        paymentId={paymentId}
      />

      <HistoryCard history={bookingDetail.changes} />

      <PassengerDetailsCard
        passengers={bookingDetail.passengers}
        paymentId={paymentId}
      />
    </div>
  );
}
